import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import intl from '@/internationalzation/intl';
import { Message } from '@docs/kd-d2c-components';
import { EventBus } from '@/utils/event-bus';
import { emitConversationEventsRefresh } from '@/utils/conversation-events-refresh';
import { GlobalEventType } from '@src/types';
import { getConversationDetailUrl } from '@src/utils/route';
import { kimBridge } from '@src/utils/kim-bridge';
import { isKim, isMobile } from '@/utils/user-agent';
import type { TaskListResponse } from '@src/services/walle/model/TaskListResponse';
import type { ConversationPageListResponse } from '@src/services/walle/model/ConversationPageListResponse';
import type { TaskDetailResponse } from '@src/services/walle/model/TaskDetailResponse';
import type { MarioSchedulerConfig } from '@src/services/walle/model/MarioSchedulerConfig';
import { formatSchedulerTimestamp } from '@/utils/scheduler-time';
import {
  marioSchedulerList,
  marioSchedulerDetail,
  marioSchedulerDelete,
  marioSchedulerSave,
  marioSchedulerRun,
  marioSchedulerEnabled,
  getConversationPageListApi,
} from '@src/services/walle/index.js';
import { parseSchedulerDateTime, toSchedulerTimestamp } from '@src/utils/scheduler-time';
import AllSchedulerModal, {
  type SchedulerSummaryItem,
} from './AllSchedulerModal/AllSchedulerModal';
import OperationSchedulerModal, {
  type SchedulerFormValues,
} from './OperationSchedulerModal/OperationSchedulerModal';
import type { SchedulerListItem } from '@src/pages/h5/scheduled-task/components/ScheduledTaskCardList';

const MASK_STYLE = {
  position: 'fixed' as const,
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--color-mask-1)',
};

const mapItem = (item: {
  config_id?: string;
  title?: string;
  next_run_time?: string | Date | number;
  enabled?: boolean;
  trigger_description?: string;
}): SchedulerSummaryItem => ({
  configId: item.config_id ?? '',
  title: item.title ?? '',
  timeText:
    item.trigger_description ??
    (parseSchedulerDateTime(item.next_run_time)?.format('YYYY/MM/DD HH:mm') ?? '-'),
  enabled: item.enabled ?? true,
});

const mapConversationItem = (item: {
  conversationId?: string;
  conversationName?: string;
  createTime?: string | Date | number;
  enabled?: boolean;
}): SchedulerSummaryItem => ({
  configId: item.conversationId ?? '',
  title: item.conversationName ?? '',
  timeText:
    parseSchedulerDateTime(item.createTime)?.format('YYYY/MM/DD HH:mm') ?? '-',
  enabled: item.enabled ?? true,
});

const fetchSchedulerLists = () =>
  Promise.all([
    marioSchedulerList({
      page: 1,
      page_size: 100,
      // 去掉enabled参数，获取启用/暂停的任务
    }).then((res: TaskListResponse) => (res?.data?.items ?? []).map(mapItem)),
    getConversationPageListApi({
      page: 1,
      page_size: 100,
      tag: 'scheduler_run',
    }).then((res: ConversationPageListResponse) =>
      (res?.data?.items ?? []).map(mapConversationItem),
    ),
  ]);

/** 从 URL 解析当前会话 ID（本组件在 Routes 外，useParams 取不到） */
const getConversationIdFromPath = (pathname: string): string => {
  const m = pathname.match(/^\/c\/([^/]+)/);
  return m ? m[1] : '';
};

/** 将详情接口返回转为 H5 编辑页所需的 SchedulerListItem */
function detailToSchedulerListItem(
  configId: string,
  d: MarioSchedulerConfig,
): SchedulerListItem {
  const tc = (d.trigger_config || {}) as {
    trigger_type?: string;
    trigger_time?: string;
    trigger_date?: string;
    weekdays?: number[];
    days_of_month?: number[];
  };
  const expireTimeStr =
    d.expire_time != null ? formatSchedulerTimestamp(d.expire_time) : undefined;
  const nextRunTimeStr =
    d.next_run_time != null ? formatSchedulerTimestamp(d.next_run_time) : undefined;
  return {
    config_id: d.config_id ?? configId,
    title: d.title ?? '',
    task: d.task ?? '',
    trigger_config: {
      trigger_type: (tc.trigger_type as SchedulerListItem['trigger_config']['trigger_type']) ?? 'once',
      trigger_time: tc.trigger_time,
      trigger_date: tc.trigger_date,
      weekdays: tc.weekdays ?? [],
      days_of_month: tc.days_of_month ?? [],
    },
    enabled: d.enabled ?? true,
    next_run_time: nextRunTimeStr !== '-' ? nextRunTimeStr : undefined,
    expire_time: expireTimeStr !== '-' ? expireTimeStr : undefined,
  };
}

/**
 * 全局「定时任务」弹窗触发器：监听 SCHEDULER_VIEW_ALL_TASK，在非会话详情页（如侧栏点击）也能唤起 AllSchedulerModal。
 * 挂载在根布局，与 conversation 内的监听并存；先响应的会打开弹窗并拉取数据。
 */
export const GlobalSchedulerModalTrigger = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const conversationId = useMemo(
    () => getConversationIdFromPath(location.pathname),
    [location.pathname],
  );
  const [allSchedulerVisible, setAllSchedulerVisible] = useState(false);
  const [allSchedulerScheduled, setAllSchedulerScheduled] = useState<
    SchedulerSummaryItem[]
  >([]);
  const [allSchedulerFinished, setAllSchedulerFinished] = useState<
    SchedulerSummaryItem[]
  >([]);
  const [schedulerEditVisible, setSchedulerEditVisible] = useState(false);
  const [schedulerEditInitialValues, setSchedulerEditInitialValues] = useState<
    Partial<SchedulerFormValues> | null
  >(null);

  useEffect(() => {
    const onViewAll = () => {
      if (isMobile) {
        navigate('/scheduled-task', {
          state: { returnUrl: location.pathname },
        });
        return;
      }
      setAllSchedulerVisible(true);
      fetchSchedulerLists()
        .then(([scheduled, finished]) => {
          setAllSchedulerScheduled(scheduled);
          setAllSchedulerFinished(finished);
        })
        .catch(() => {
          setAllSchedulerScheduled([]);
          setAllSchedulerFinished([]);
        });
    };
    EventBus.on(GlobalEventType.SCHEDULER_VIEW_ALL_TASK, onViewAll);
    return () => {
      EventBus.off(GlobalEventType.SCHEDULER_VIEW_ALL_TASK, onViewAll);
    };
  }, [navigate, location.pathname]);

  // H5：从对话卡片「编辑任务」跳转到 H5 编辑页
  useEffect(() => {
    if (!isMobile) return;
    const onSchedulerEdit = (payload: { configId: string }) => {
      const { configId } = payload;
      if (!configId) return;
      marioSchedulerDetail({ config_id: configId })
        .then((res: TaskDetailResponse) => {
          const d = res?.data as MarioSchedulerConfig | undefined;
          if (!d) return;
          const item = detailToSchedulerListItem(configId, d);
          navigate('/scheduled-task/edit', {
            state: { item, returnUrl: location.pathname },
          });
        })
        .catch(() => { });
    };
    EventBus.on(GlobalEventType.SCHEDULER_EDIT_TASK, onSchedulerEdit);
    return () => {
      EventBus.off(GlobalEventType.SCHEDULER_EDIT_TASK, onSchedulerEdit);
    };
  }, [isMobile, navigate, location.pathname]);

  if (!allSchedulerVisible && !schedulerEditVisible) {
    return null;
  }

  const showEdit = schedulerEditVisible && schedulerEditInitialValues !== null;

  return (
    <div
      className="conversation-detail-scheduler-mask"
      style={MASK_STYLE}
    >
      <div>
        {showEdit ? (
          <OperationSchedulerModal
            initialValues={schedulerEditInitialValues!}
            onClose={() => {
              setSchedulerEditVisible(false);
              setSchedulerEditInitialValues(null);
            }}
            onConfirm={async (values) => {
              const {
                configId,
                title,
                task,
                enabled = true,
                nextRunTime,
                expireTime,
                createTime,
                updateTime,
                triggerConfig,
              } = values;
              const {
                triggerType = 'once',
                triggerTime,
                triggerDate,
                weekdays = [],
                daysOfMonth = [],
              } = triggerConfig || {};
              try {
                await marioSchedulerSave({
                  config_id: configId ?? '',
                  title: title ?? '',
                  task: task ?? '',
                  trigger_config: {
                    trigger_type: triggerType,
                    trigger_time: triggerTime ? triggerTime.format('HH:mm') : '',
                    trigger_date: triggerDate
                      ? triggerDate.format('YYYY-MM-DD')
                      : '',
                    weekdays,
                    days_of_month: daysOfMonth,
                  },
                  enabled,
                  next_run_time: toSchedulerTimestamp(nextRunTime),
                  expire_time: toSchedulerTimestamp(expireTime),
                  create_time: toSchedulerTimestamp(createTime),
                  update_time: toSchedulerTimestamp(updateTime),
                });
                setSchedulerEditVisible(false);
                setSchedulerEditInitialValues(null);
                emitConversationEventsRefresh({
                  conversationId,
                  source: 'scheduler_edit_modal',
                  reason: 'save',
                });
                fetchSchedulerLists().then(([scheduled, finished]) => {
                  setAllSchedulerScheduled(scheduled);
                  setAllSchedulerFinished(finished);
                });
              } catch (error) {
                console.error(error);
              }
            }}
            onDelete={() => {
              const id = schedulerEditInitialValues?.configId ?? '';
              marioSchedulerDelete({ config_id: id })
                .then(() => {
                  Message.success({ message: intl.t('删除成功') });
                  setAllSchedulerScheduled((prev) =>
                    prev.filter((item) => item.configId !== id),
                  );
                  setSchedulerEditVisible(false);
                  setSchedulerEditInitialValues(null);
                  emitConversationEventsRefresh({
                    conversationId,
                    source: 'scheduler_edit_modal',
                    reason: 'delete',
                  });
                })
                .catch((error: unknown) => {
                  console.error(error);
                });
            }}
          />
        ) : (
          <AllSchedulerModal
            visible={allSchedulerVisible}
            scheduledTasks={allSchedulerScheduled}
            finishedTasks={allSchedulerFinished}
            onClose={() => setAllSchedulerVisible(false)}
            onCreateTask={() => {
              setSchedulerEditInitialValues({});
              setSchedulerEditVisible(true);
            }}
            onRunNow={(id) => {
              marioSchedulerRun({ config_id: id }).then(
                (res: { data?: { conversation_id?: string } }) => {
                  const newConversationId = res?.data?.conversation_id;
                  setAllSchedulerVisible(false);
                  if (newConversationId) {
                    if (isMobile && isKim) {
                      kimBridge.openMario({
                        type: 1,
                        url: getConversationDetailUrl(newConversationId),
                      });
                    } else {
                      navigate(getConversationDetailUrl(newConversationId, false));
                    }
                  }
                  emitConversationEventsRefresh({
                    conversationId: newConversationId ?? conversationId,
                    source: 'scheduler_all_modal',
                    reason: 'run',
                  });
                },
              );
            }}
            onEdit={(id) => {
              marioSchedulerDetail({ config_id: id })
                .then((res: TaskDetailResponse) => {
                  const d = res.data as MarioSchedulerConfig;
                  if (!d) return;
                  const tc = d.trigger_config;
                  setSchedulerEditInitialValues({
                    configId: d.config_id ?? id,
                    title: d.title ?? '',
                    task: d.task ?? '',
                    triggerConfig: {
                      triggerType: (tc?.trigger_type ?? 'once') as SchedulerFormValues['triggerConfig']['triggerType'],
                      triggerTime: tc?.trigger_time ? dayjs(tc.trigger_time, 'HH:mm') : null,
                      triggerDate: tc?.trigger_date ? dayjs(tc.trigger_date) : null,
                      weekdays: tc?.weekdays ?? [],
                      daysOfMonth: tc?.days_of_month ?? [],
                    },
                    enabled: d.enabled ?? true,
                    nextRunTime: parseSchedulerDateTime(d.next_run_time) ?? null,
                    expireTime: parseSchedulerDateTime(d.expire_time) ?? null,
                    createTime: parseSchedulerDateTime(d.create_time) ?? null,
                    updateTime: parseSchedulerDateTime(d.update_time) ?? null,
                  });
                  setSchedulerEditVisible(true);
                })
                .catch(() => { });
            }}
            onDelete={(id) => {
              marioSchedulerDelete({ config_id: id })
                .then(() => {
                  Message.success({ message: intl.t('删除成功') });
                  setAllSchedulerScheduled((prev) =>
                    prev.filter((item) => item.configId !== id),
                  );
                  emitConversationEventsRefresh({
                    conversationId,
                    source: 'scheduler_all_modal',
                    reason: 'delete',
                  });
                })
                .catch((error: unknown) => {
                  console.error(error);
                });
            }}
            onToggleEnabled={(id, enabled) => {
              marioSchedulerEnabled({ config_id: id }, { enabled }).then(() => {
                setAllSchedulerScheduled((prev) =>
                  prev.map((item) =>
                    item.configId === id ? { ...item, enabled } : item,
                  ),
                );
                emitConversationEventsRefresh({
                  conversationId,
                  source: 'scheduler_all_modal',
                  reason: enabled ? 'enable' : 'disable',
                });
              });
            }}
          />
        )}
      </div>
    </div>
  );
};
